import { AxiosProgressEvent } from 'axios';
// import { RESET } from 'jotai/utils';
import {
  deleteFileRequestApiCall,
  fileProcessStatusCheckApiCall,
  getSessionDetailsApiCall,
  intiateFileProcessApiCall,
  intiateFileUploadApiCall,
  uploadFileApiCall,
  getFileRepositoryApiCall,
  getFileDownloadUrlApiCall,
  downloadFileFromPresignedUrl,
} from '../../../services/FileUpload';
import {
  fileUploadSegment,
  FileUploadStage,
  FileError,
  FileState,
} from '../stateSegments/fileUploadSegment';
import {
  createInitialFileState,
  updateFileState,
  addFileToState,
  removeFileFromState,
  getActiveFileIds,
  areAllFilesComplete,
} from '../utils/fileUploadStateHelpers';
import { translate } from '../../../localization';

import store from '../../markettingContentGeneration';
import {
  ANALYTICS,
  fileProcessStatuses,
  fileUploadStatuses,
} from '../../../constants/common';
import {
  trackFileExtractionComplete,
  trackFileExtractionError,
  trackFileExtractionInitiate,
  trackFileUploadComplete,
  trackFileUploadError,
  trackFileUploadInitiate,
} from '../../../services/Analytics';
import {
  trackfileUploadUsage,
  trackFileRepoLimitReached,
  trackFileUploadSize,
  trackFileDelete,
  trackFileDownload,
} from '../../../services/Analytics/fileUpload';
import { getEncodedUserNtid } from '../../markettingContentGeneration/helpers/common';
import { trackfileExtractionUsage } from '../../../services/Analytics/fileExtraction';
import { loadPromptFieldOptions } from '../../../services/Content';
import Logger from '../../../utils/logger';
import { FormFields } from '../../../types/PromptForm';
import { CannedParametersResult } from '../../../types/Content';
import { actionsSegment } from '../stateSegments/actionsSegment';
import { ExtractionStatus } from '../../../types/FileUpload';

let controller = new AbortController();
let { signal } = controller;

/**
 * Maps extraction status from file repository to file error state.
 * Uses the same error mapping logic as validateFileProcess for consistency.
 */
const mapExtractionStatusToError = (
  extractionStatus: ExtractionStatus | undefined
): { status: FileUploadStage; error?: FileError } => {
  if (!extractionStatus) {
    return { status: 'completed' };
  }

  const { status, errors } = extractionStatus;

  // Check if status indicates completion
  if (status === fileProcessStatuses.FILE_PROCESS_COMPLETED) {
    return { status: 'completed' };
  }

  // Check if status indicates in progress
  if (
    status === fileProcessStatuses.FILE_PROCESS_INITIATED ||
    status === fileProcessStatuses.FILE_PROCESS_IN_PROGRESS
  ) {
    return { status: 'extracting' };
  }

  // Handle error statuses
  if (
    status === fileProcessStatuses.FILE_PROCESS_ERROR ||
    status === fileProcessStatuses.UNSUPPORTED_FILE_LANG_ERROR
  ) {
    let errorTitle: string;
    let errorMessage: string;

    // Map error types from errors array to localized messages
    if (errors && errors.length > 0) {
      const errorType = errors[0];

      switch (errorType) {
        case 'TypeError':
          errorTitle = translate('file_upload_error_type_error_title');
          errorMessage = translate('file_upload_error_type_error_message');
          break;
        case 'The file is empty':
          errorTitle = translate('file_upload_error_empty_file_title');
          errorMessage = translate('file_upload_error_empty_file_message');
          break;
        case 'Unsupported content type':
          errorTitle = translate(
            'file_upload_error_unsupported_content_type_title'
          );
          errorMessage = translate(
            'file_upload_error_unsupported_content_type_message'
          );
          break;
        case 'Error Processing A Document':
        case 'Not Enough Claims':
          errorTitle = translate('file_upload_error_processing_document_title');
          errorMessage = translate(
            'file_upload_error_processing_document_message'
          );
          break;
        default:
          errorTitle = translate('file_upload_error_default_title');
          errorMessage = errorType;
          break;
      }
    } else if (status === fileProcessStatuses.UNSUPPORTED_FILE_LANG_ERROR) {
      errorTitle = translate(
        'file_upload_error_unsupported_content_type_title'
      );
      errorMessage = translate('file_upload_language_not_supported_message');
    } else {
      errorTitle = translate('file_upload_error_default_title');
      errorMessage = translate('file_upload_general_error_message');
    }

    return {
      status: 'error',
      error: {
        stage: 'extracting',
        title: errorTitle,
        message: errorMessage,
      },
    };
  }

  // Default to completed for unknown statuses
  return { status: 'completed' };
};

// Lock to prevent race conditions during rapid file uploads
let isUploadLocked = false;
const pendingFiles: File[] = [];

const createOnUploadProgress = (fileId: string) => {
  return (progressEvent: AxiosProgressEvent) => {
    const percentCompleted = Math.round(
      (progressEvent.loaded * 100) / (progressEvent.total || 100)
    );
    store.set(fileUploadSegment, prev =>
      updateFileState(prev, fileId, {
        uploadProgress: percentCompleted,
      })
    );
  };
};

/**
 * Uploads a file to a private URL using pre-signed URL.
 * Updates the individual file's state during the upload process.
 */
export const uploadFileToPrivateUrl = async (
  preSignedURL: string,
  file: File,
  fileId: string,
  fileName: string
): Promise<void> => {
  // Set the file's upload state
  store.set(fileUploadSegment, prev =>
    updateFileState(prev, fileId, {
      status: 'uploading',
      error: undefined,
    })
  );

  const onUploadProgress = createOnUploadProgress(fileId);

  try {
    if (import.meta.env.MODE === 'development') {
      setTimeout(() => {
        onUploadProgress({
          loaded: 50,
          total: 100,
          bytes: 0,
          lengthComputable: false,
        });
      }, 500);
    }

    const response = await uploadFileApiCall(
      preSignedURL,
      file,
      signal,
      onUploadProgress
    );

    if (response.status === 200) {
      // Update the file's state to indicate upload complete
      store.set(fileUploadSegment, prev =>
        updateFileState(prev, fileId, {
          status: 'extracting',
          uploadProgress: 100,
          error: undefined,
        })
      );

      const { sessionId } = store.get(fileUploadSegment);

      // track analytics for file upload complete
      trackFileUploadComplete(ANALYTICS.FILE_UPLOAD_FORM_NAME);

      intiateFileProcess(
        sessionId as string,
        fileId,
        fileName,
        fileUploadStatuses.FILE_UPLOAD_COMPLETED
      );
    } else {
      store.set(fileUploadSegment, prev =>
        updateFileState(prev, fileId, {
          status: 'error',
          uploadProgress: 0,
          error: {
            stage: 'uploading',
            message: translate('file_upload_initiate_error_message'),
            title: translate('file_upload_initiate_error_title'),
          },
        })
      );

      // track analytics for file upload error
      trackFileUploadError(ANALYTICS.FILE_UPLOAD_FORM_NAME);
    }
  } catch {
    // Update the file's state to indicate upload error
    store.set(fileUploadSegment, prev =>
      updateFileState(prev, fileId, {
        status: 'error',
        uploadProgress: 0,
        error: {
          stage: 'uploading',
          message: translate('file_upload_initiate_error_message'),
          title: translate('file_upload_initiate_error_title'),
        },
      })
    );

    // track analytics for file upload error
    trackFileUploadError(ANALYTICS.FILE_UPLOAD_FORM_NAME);
  }
};

/**
 * Processes files in the upload queue
 */
const processUploadQueue = async () => {
  const { sessionId, isInitializingSession, uploadQueue } =
    store.get(fileUploadSegment);

  // If already initializing, wait for it to complete
  if (isInitializingSession) {
    return;
  }

  // If no session and not initializing, start with first file
  if (!sessionId && uploadQueue.length > 0) {
    await initializeSessionWithFirstFile();
    // After session is initialized, process remaining files
    await processRemainingQueue();
  } else if (sessionId && uploadQueue.length > 0) {
    // Session exists, process all queued files
    await processRemainingQueue();
  }
};

/**
 * Initializes session with the first file in queue
 */
const initializeSessionWithFirstFile = async () => {
  const { uploadQueue } = store.get(fileUploadSegment);

  if (uploadQueue.length === 0) return;

  const firstQueuedFile = uploadQueue[0];

  // Mark session initialization started
  store.set(fileUploadSegment, prev => ({
    ...prev,
    isInitializingSession: true,
  }));

  try {
    // Call API WITHOUT sessionId to create new session
    const { success, data } = await intiateFileUploadApiCall(
      undefined, // No sessionId
      undefined,
      [firstQueuedFile.file.name]
    );

    if (success && data) {
      const { sessionId: returnedSessionId, fileUploads } = data;
      const fileUpload = fileUploads?.[0];

      if (
        fileUpload &&
        fileUpload.status !== fileUploadStatuses.FILE_UPLOAD_ERROR
      ) {
        // Session created successfully
        const fileState = createInitialFileState(
          fileUpload.fileId,
          fileUpload.fileName,
          firstQueuedFile.file.size
        );

        // Update state: set sessionId, add file, remove from queue
        store.set(fileUploadSegment, prev => ({
          ...addFileToState(prev, fileState),
          sessionId: returnedSessionId,
          isInitializingSession: false,
          uploadQueue: prev.uploadQueue.filter(
            q => q.queueId !== firstQueuedFile.queueId
          ),
        }));

        // Start upload
        await uploadFileToPrivateUrl(
          fileUpload.preSignedURL,
          firstQueuedFile.file,
          fileUpload.fileId,
          fileUpload.fileName
        );
      } else {
        throw new Error('File upload initialization returned error status');
      }
    } else {
      throw new Error('Failed to initialize session');
    }
  } catch (error) {
    // Session initialization failed
    Logger.error(new Error(`Session initialization failed: ${String(error)}`));

    // track analytics
    trackFileUploadError(ANALYTICS.FILE_UPLOAD_FORM_NAME);

    // Create a failed file entry to show in UI
    const tempFileId = `failed-init-${Date.now()}`;
    const fileState = createInitialFileState(
      tempFileId,
      firstQueuedFile.file.name,
      firstQueuedFile.file.size
    );
    fileState.status = 'error';
    fileState.error = {
      stage: 'uploading',
      message: translate('file_upload_session_init_error_message'),
      title: translate('file_upload_session_init_error_title'),
    };

    // Remove from queue and add as failed file
    store.set(fileUploadSegment, prev => ({
      ...addFileToState(prev, fileState),
      isInitializingSession: false,
      uploadQueue: prev.uploadQueue.filter(
        q => q.queueId !== firstQueuedFile.queueId
      ),
    }));

    // IMPORTANT: Try to initialize with next file in queue if any
    const remainingQueue = store.get(fileUploadSegment).uploadQueue;
    if (remainingQueue.length > 0) {
      // Retry with next file
      await initializeSessionWithFirstFile();
    }
  }
};

/**
 * Processes remaining files in queue (after session is initialized)
 */
const processRemainingQueue = async () => {
  const { sessionId, uploadQueue } = store.get(fileUploadSegment);

  if (!sessionId || uploadQueue.length === 0) return;

  // Process all queued files
  const queueCopy = [...uploadQueue];

  for (const queuedFile of queueCopy) {
    await uploadQueuedFile(queuedFile);
  }
};

/**
 * Uploads a single queued file
 */
const uploadQueuedFile = async (queuedFile: {
  file: File;
  queueId: string;
}) => {
  const { sessionId } = store.get(fileUploadSegment);

  if (!sessionId) {
    Logger.error(new Error('Attempted to upload file without session ID'));
    return;
  }

  try {
    // Call API WITH sessionId
    const { success, data } = await intiateFileUploadApiCall(
      sessionId,
      undefined,
      [queuedFile.file.name]
    );

    if (success && data) {
      const fileUpload = data.fileUploads?.[0];

      if (
        fileUpload &&
        fileUpload.status !== fileUploadStatuses.FILE_UPLOAD_ERROR
      ) {
        // Create file state
        const fileState = createInitialFileState(
          fileUpload.fileId,
          fileUpload.fileName,
          queuedFile.file.size
        );

        // Add to files Map and remove from queue
        store.set(fileUploadSegment, prev => ({
          ...addFileToState(prev, fileState),
          uploadQueue: prev.uploadQueue.filter(
            q => q.queueId !== queuedFile.queueId
          ),
        }));

        // Start upload
        await uploadFileToPrivateUrl(
          fileUpload.preSignedURL,
          queuedFile.file,
          fileUpload.fileId,
          fileUpload.fileName
        );
      } else {
        throw new Error('File upload returned error status');
      }
    } else {
      throw new Error('Failed to initiate file upload');
    }
  } catch (error) {
    Logger.error(new Error(`File upload failed: ${String(error)}`));

    // track analytics
    trackFileUploadError(ANALYTICS.FILE_UPLOAD_FORM_NAME);

    // Create failed file entry
    const tempFileId = `failed-upload-${Date.now()}`;
    const fileState = createInitialFileState(
      tempFileId,
      queuedFile.file.name,
      queuedFile.file.size
    );
    fileState.status = 'error';
    fileState.error = {
      stage: 'uploading',
      message: translate('file_upload_initiate_error_message'),
      title: translate('file_upload_initiate_error_title'),
    };

    // Remove from queue and add as failed file
    store.set(fileUploadSegment, prev => ({
      ...addFileToState(prev, fileState),
      uploadQueue: prev.uploadQueue.filter(
        q => q.queueId !== queuedFile.queueId
      ),
    }));
  }
};

/**
 * Adds files to the upload queue and starts processing them
 * Uses a lock to prevent race conditions during rapid file drops
 */
export const intiateFileUpload = async (files: File[]) => {
  const maxFiles = 7;

  // If locked, add to pending and return - they'll be processed when lock is released
  if (isUploadLocked) {
    pendingFiles.push(...files);
    return;
  }

  // Acquire lock
  isUploadLocked = true;

  try {
    // Combine incoming files with any pending files
    const allFiles = [...files, ...pendingFiles];
    pendingFiles.length = 0; // Clear pending array

    // Get current state to check file count
    const currentState = store.get(fileUploadSegment);
    const currentFileCount = currentState.files.size; // Use .size for Map
    const queuedFileCount = currentState.uploadQueue.length; // Include files already in queue
    const totalCurrentFiles = currentFileCount + queuedFileCount;

    // Calculate remaining slots (considering both uploaded and queued files)
    const remainingSlots = maxFiles - totalCurrentFiles;

    // Early return if limit is already reached or would be exceeded
    if (remainingSlots <= 0) {
      Logger.info('File upload limit reached. Cannot add more files.');
      return;
    }

    // Only queue files that fit within the remaining slots
    const filesToQueue = allFiles.slice(0, remainingSlots);

    if (filesToQueue.length === 0) {
      Logger.info('No files to queue - limit would be exceeded.');
      return;
    }

    // Add files to queue
    const queuedFiles = filesToQueue.map(file => ({
      file,
      queueId: `queue-${Date.now()}-${Math.random()}`,
    }));

    store.set(fileUploadSegment, prev => ({
      ...prev,
      uploadQueue: [...prev.uploadQueue, ...queuedFiles],
    }));

    // analytics tracking for file upload initiate
    trackFileUploadInitiate(ANALYTICS.FILE_UPLOAD_FORM_NAME);
    trackfileUploadUsage(ANALYTICS.FILE_UPLOAD_FORM_NAME, getEncodedUserNtid());

    // Track file upload sizes
    filesToQueue.forEach(file => {
      const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);
      trackFileUploadSize(`${fileSizeInMB}MB`);
    });

    // Track when file repository limit is reached (7 files)
    const newFileCount = totalCurrentFiles + filesToQueue.length;
    if (newFileCount >= maxFiles && totalCurrentFiles < maxFiles) {
      trackFileRepoLimitReached();
    }

    // Start processing the queue
    await processUploadQueue();
  } finally {
    // Release lock
    isUploadLocked = false;

    // Process any files that accumulated while locked
    if (pendingFiles.length > 0) {
      const pending = [...pendingFiles];
      pendingFiles.length = 0;
      await intiateFileUpload(pending);
    }
  }
};

const fetchActions = async () => {
  try {
    const response = await loadPromptFieldOptions(FormFields.ACTION, {});
    const { data, success } = response as CannedParametersResult;
    if (success) {
      store.set(actionsSegment, data.items);
      store.set(fileUploadSegment, prev => ({
        ...prev,
        sessionLoaded: true,
        sessionLoading: false,
      }));
    }
  } catch (error) {
    Logger.info(error);
  }
};

export const validateFileProcess = async (): Promise<boolean> => {
  try {
    const { sessionId, files } = store.get(fileUploadSegment);

    // Get active file IDs that need polling
    const activeFileIds = getActiveFileIds(files);

    if (activeFileIds.length === 0) {
      return false; // Nothing to poll
    }

    const response = await fileProcessStatusCheckApiCall(
      sessionId,
      activeFileIds
    );

    if (response.success && response.data) {
      const { fileProcessStatusUpdates } = response.data;
      // Process status updates for each file
      let hasCompletedFile = false;
      let hasNewlyCompletedFile = false; // Track if any file just completed in this cycle

      for (const statusUpdate of fileProcessStatusUpdates || []) {
        const {
          fileId,
          fileName,
          fileProcessStatus: status,
          fileProcessPercentage,
          errors,
        } = statusUpdate;

        const fileProcessedError = false;

        // Check if this file was NOT completed before but is now completed
        const currentFile = files.get(fileId);
        const wasNotCompleted = currentFile?.status !== 'completed';
        const isNowCompleted =
          status === fileProcessStatuses.FILE_PROCESS_COMPLETED;

        if (wasNotCompleted && isNowCompleted) {
          hasNewlyCompletedFile = true;
        }

        // Map error types from errors array to localized messages
        let errorMessage: string | undefined;
        let errorTitle: string | undefined;

        if (errors && errors.length > 0) {
          const errorType = errors[0]; // First error message indicates the type

          switch (errorType) {
            case 'TypeError':
              errorTitle = translate('file_upload_error_type_error_title');
              errorMessage = translate('file_upload_error_type_error_message');
              break;
            case 'The file is empty':
              errorTitle = translate('file_upload_error_empty_file_title');
              errorMessage = translate('file_upload_error_empty_file_message');
              break;
            case 'Unsupported content type':
              errorTitle = translate(
                'file_upload_error_unsupported_content_type_title'
              );
              errorMessage = translate(
                'file_upload_error_unsupported_content_type_message'
              );
              break;
            case 'Error Processing A Document':
            case 'Not Enough Claims':
              errorTitle = translate(
                'file_upload_error_processing_document_title'
              );
              errorMessage = translate(
                'file_upload_error_processing_document_message'
              );
              break;
            default:
              // Use the error message as-is if no specific mapping
              errorTitle = translate('file_upload_error_default_title');
              errorMessage = errorType;
              break;
          }
        } else if (status === fileProcessStatuses.FILE_PROCESS_ERROR) {
          errorTitle = translate('file_upload_error_default_title');
          errorMessage = translate('file_upload_general_error_message');
        } else if (status === fileProcessStatuses.UNSUPPORTED_FILE_LANG_ERROR) {
          errorTitle = translate(
            'file_upload_error_unsupported_content_type_title'
          );
          errorMessage = translate(
            'file_upload_language_not_supported_message'
          );
        }

        // Build file-specific state updates
        let fileStatus: FileUploadStage = 'uploading';
        let uploadProgress = 0;
        let extractionProgress = 0;
        let fileError: FileError | undefined = undefined;

        // Determine status based on API response
        if (status === fileUploadStatuses.FILE_UPLOAD_INITIATED) {
          fileStatus = 'uploading';
        } else if (
          status === fileUploadStatuses.FILE_UPLOAD_COMPLETED ||
          status === fileProcessStatuses.FILE_PROCESS_IN_PROGRESS
        ) {
          fileStatus = 'extracting';
          uploadProgress = 100;
          extractionProgress = fileProcessPercentage || 0;
        } else if (
          status === fileProcessStatuses.FILE_PROCESS_COMPLETED &&
          !fileProcessedError
        ) {
          // Start with analyzing, will transition to completed below
          fileStatus = 'analyzing';
          uploadProgress = 100;
          extractionProgress = 100;
        } else if (status === fileUploadStatuses.FILE_UPLOAD_ERROR) {
          fileStatus = 'error';
          fileError = {
            stage: 'uploading',
            message:
              errorMessage || translate('file_upload_error_default_message'),
            title: errorTitle || translate('file_upload_error_default_title'),
          };
        } else if (
          status === fileProcessStatuses.FILE_PROCESS_ERROR ||
          status === fileProcessStatuses.UNSUPPORTED_FILE_LANG_ERROR ||
          fileProcessedError
        ) {
          fileStatus = 'error';
          fileError = {
            stage: 'extracting',
            message:
              errorMessage || translate('file_upload_general_error_message'),
            title: errorTitle || translate('file_upload_error_default_title'),
          };
        }

        // Handle completed files - transition from analyzing to completed
        if (
          status === fileProcessStatuses.FILE_PROCESS_COMPLETED &&
          !fileProcessedError
        ) {
          trackFileExtractionComplete(ANALYTICS.FILE_EXTRACTION_FORM_NAME);

          // Transition to 'completed' status
          fileStatus = 'completed';

          // Fetch actions only once for first completed file
          if (!hasCompletedFile) {
            await fetchActions();
          }

          // Mark that we have a completed file
          hasCompletedFile = true;
        } else if (status === fileProcessStatuses.FILE_PROCESS_ERROR) {
          trackFileExtractionError(ANALYTICS.FILE_EXTRACTION_FORM_NAME);
        }

        // Create file updates object
        const fileUpdates = {
          fileName,
          status: fileStatus,
          uploadProgress,
          extractionProgress,
          error: fileError,
        };

        // Update individual file state
        store.set(fileUploadSegment, prev =>
          updateFileState(prev, fileId, fileUpdates)
        );
      }

      // Check if we should continue polling
      const updatedFiles = store.get(fileUploadSegment).files;
      const shouldContinuePolling = !areAllFilesComplete(updatedFiles);

      if (!shouldContinuePolling) {
        // All files are complete - stop polling
        if (window.uploadInterval) {
          clearInterval(window.uploadInterval);
          window.uploadInterval = null;
        }

        // Fetch repository data one final time when all files are complete
        await fetchFileRepository();
      } else if (hasNewlyCompletedFile) {
        // Only fetch repository when a file actually completed in this polling cycle
        // This updates thumbnails and metadata for files that just completed
        await fetchFileRepository();
      }

      return shouldContinuePolling;
    }

    // API call failed but didn't throw - update all active files with error
    const { files: currentFiles } = store.get(fileUploadSegment);
    const failedActiveFileIds = getActiveFileIds(currentFiles);

    failedActiveFileIds.forEach(fileId => {
      store.set(fileUploadSegment, prev =>
        updateFileState(prev, fileId, {
          status: 'error',
          error: {
            stage: 'extracting',
            message: translate('file_process_error_message'),
            title: translate('file_upload_error_state_title'),
          },
        })
      );
    });

    if (window.uploadInterval) {
      clearInterval(window.uploadInterval);
    }
    window.uploadInterval = null;

    // track analytics for file extraction error
    trackFileExtractionError(ANALYTICS.FILE_EXTRACTION_FORM_NAME);
    return false;
  } catch {
    // Network error or exception - update all active files with error
    const { files: currentFiles } = store.get(fileUploadSegment);
    const errorActiveFileIds = getActiveFileIds(currentFiles);

    errorActiveFileIds.forEach(fileId => {
      store.set(fileUploadSegment, prev =>
        updateFileState(prev, fileId, {
          status: 'error',
          error: {
            stage: 'extracting',
            message: translate('file_process_error_message'),
            title: translate('file_upload_error_state_title'),
          },
        })
      );
    });

    if (window.uploadInterval) {
      clearInterval(window.uploadInterval);
    }
    window.uploadInterval = null;

    // track analytics for file extraction error
    trackFileExtractionError(ANALYTICS.FILE_EXTRACTION_FORM_NAME);
    return false;
  }
};

export const intiateFileProcess = async (
  sessionId: string,
  fileId: string,
  fileName: string,
  fileUploadStatus: string
) => {
  store.set(fileUploadSegment, prev =>
    updateFileState(prev, fileId, {
      status: 'extracting',
      error: undefined,
    })
  );

  // track analytics for file extraction initiate
  trackFileExtractionInitiate(ANALYTICS.FILE_EXTRACTION_FORM_NAME);

  // track the ntid for file extraction
  trackfileExtractionUsage(
    ANALYTICS.FILE_EXTRACTION_FORM_NAME,
    getEncodedUserNtid()
  );

  const { success, data } = await intiateFileProcessApiCall(
    sessionId,
    fileId,
    fileName,
    fileUploadStatus
  );

  if (success && data) {
    const { fileProcess } = data;

    if (
      fileProcess.fileProcessStatus !== fileProcessStatuses.FILE_PROCESS_ERROR
    ) {
      store.set(fileUploadSegment, prev =>
        updateFileState(prev, fileId, {
          status: 'extracting',
        })
      );

      // Only start polling if not already polling
      if (!window.uploadInterval) {
        const continuePolling = await validateFileProcess();
        if (continuePolling) {
          window.uploadInterval = setInterval(() => {
            validateFileProcess();
          }, 5000);
        }
      }
    } else {
      store.set(fileUploadSegment, prev =>
        updateFileState(prev, fileId, {
          status: 'error',
          error: {
            stage: 'extracting',
            message: translate('file_process_error_message'),
            title: translate('file_upload_error_state_title'),
          },
        })
      );

      // track analytics for file extraction error
      trackFileExtractionError(ANALYTICS.FILE_EXTRACTION_FORM_NAME);
    }
  } else {
    store.set(fileUploadSegment, prev =>
      updateFileState(prev, fileId, {
        status: 'error',
        error: {
          stage: 'extracting',
          message: translate('file_process_error_message'),
          title: translate('file_upload_error_state_title'),
        },
      })
    );

    // track analytics for file extraction error
    trackFileExtractionError(ANALYTICS.FILE_EXTRACTION_FORM_NAME);
  }
};

export const deleteFile = async (sessionId: string, fileIds: string[]) => {
  if (!sessionId || fileIds.length === 0) {
    return;
  }

  // Mark files as deleting (preserve error state for UI display)
  fileIds.forEach(fileId => {
    store.set(fileUploadSegment, prev =>
      updateFileState(prev, fileId, {
        isDeleting: true,
      })
    );
  });

  // Abort ongoing uploads
  controller.abort();
  controller = new AbortController();
  signal = controller.signal;

  // Stop polling if needed
  if (window.uploadInterval) {
    clearInterval(window.uploadInterval);
    window.uploadInterval = null;
  }

  const { success, data } = await deleteFileRequestApiCall(sessionId, fileIds);

  if (success && data) {
    const { fileDeleteStatus } = data;

    // Process deletion results
    fileDeleteStatus.forEach(deleteStatus => {
      const { fileId, fileStatus } = deleteStatus;

      if (fileStatus === 'FILE_DELETE_COMPLETED') {
        // Remove file from state
        store.set(fileUploadSegment, prev => removeFileFromState(prev, fileId));

        // Track analytics for file delete
        trackFileDelete();
      } else {
        // Deletion failed for this file - just clear deleting state, don't update error
        store.set(fileUploadSegment, prev =>
          updateFileState(prev, fileId, {
            isDeleting: false,
          })
        );
      }
    });

    // After processing deletion results, check if we need to resume polling
    const { files: remainingFiles } = store.get(fileUploadSegment);
    const activeFileIds = getActiveFileIds(remainingFiles);

    if (activeFileIds.length > 0) {
      // Resume polling for remaining active files
      const continuePolling = await validateFileProcess();
      if (continuePolling) {
        window.uploadInterval = setInterval(() => {
          validateFileProcess();
        }, 5000);
      }
    }
  } else {
    // Delete API failed - just clear deleting state for all files
    fileIds.forEach(fileId => {
      store.set(fileUploadSegment, prev =>
        updateFileState(prev, fileId, {
          isDeleting: false,
        })
      );
    });

    // Resume polling for any remaining active files (that weren't being deleted)
    const { files: remainingFiles } = store.get(fileUploadSegment);
    const activeFileIds = getActiveFileIds(remainingFiles);

    if (activeFileIds.length > 0) {
      // Resume polling since deletion failed and files are still active
      const continuePolling = await validateFileProcess();
      if (continuePolling) {
        window.uploadInterval = setInterval(() => {
          validateFileProcess();
        }, 5000);
      }
    }
  }
};

export const getSessionDetails = async (): Promise<{
  sessionId: string;
  restore: boolean;
  contextSessionId: string;
}> => {
  store.set(fileUploadSegment, prev => {
    return {
      ...prev,
      sessionLoading: true,
      error: null,
      success: null,
    };
  });
  const { success, data } = await getSessionDetailsApiCall();
  if (success && data) {
    const { userSessionData } = data;
    const lastSession = userSessionData.activeSessions.find(
      session => session.isLastActiveSession
    );

    const sessions = userSessionData?.activeSessions || [];

    const extractionSession = sessions.find(
      session => session.type === 'CONTENT_EXTRACTION'
    );

    let functionResponse = {
      sessionId: '',
      contextSessionId: '',
      restore: false,
    };
    if (lastSession) {
      functionResponse = {
        ...functionResponse,
        contextSessionId: lastSession.id,
        restore: true,
      };
    }
    if (extractionSession) {
      store.set(fileUploadSegment, prev => {
        return {
          ...prev,
          sessionId: extractionSession.id,
          // Store session restore information
          restore: functionResponse.restore,
          contextSessionId: functionResponse.contextSessionId,
          sessionContextRestored: !functionResponse.contextSessionId,
          sessionDetailsFetched: true,
        };
      });
      functionResponse = {
        ...functionResponse,
        sessionId: extractionSession.id,
      };

      // Fetch file repository after session ID is set
      await fetchFileRepository();

      return functionResponse;
    }
    store.set(fileUploadSegment, prev => {
      return {
        ...prev,
        sessionId: '',
        // Store session restore information
        restore: functionResponse.restore,
        contextSessionId: functionResponse.contextSessionId,
        sessionContextRestored: !functionResponse.contextSessionId,
        sessionDetailsFetched: true,
      };
    });
    return functionResponse;
  }
  store.set(fileUploadSegment, prev => {
    return {
      ...prev,
      sessionId: '',
      sessionLoading: false,
      sessionLoaded: false,
      error: null,
      sessionDetailsFetched: true,
    };
  });
  return { contextSessionId: '', sessionId: '', restore: false };
};

export const markSessionContextAsRestored = () => {
  store.set(fileUploadSegment, prev => ({
    ...prev,
    sessionContextRestored: true,
  }));
};

/**
 * Fetches file repository data and updates file states with metadata, thumbnails, and custom names.
 * Should be called when component mounts and when any file completes processing.
 */
export const fetchFileRepository = async (): Promise<boolean> => {
  const { sessionId, files } = store.get(fileUploadSegment);

  if (!sessionId) {
    return false;
  }

  // Set loading state
  store.set(fileUploadSegment, prev => ({
    ...prev,
    isLoadingRepository: true,
    repositoryFetchError: false,
  }));

  try {
    const { success, data } = await getFileRepositoryApiCall(sessionId);

    if (success && data?.fileRepositoryDetails) {
      // Create a set of file IDs from the repository response
      const repositoryFileIds = new Set(
        data.fileRepositoryDetails.map(repoFile => repoFile.fileId)
      );

      // Remove files from state that are no longer in the repository
      // BUT keep files that are still processing (not completed yet)
      const currentFileIds = Array.from(files.keys());
      currentFileIds.forEach(fileId => {
        const fileState = files.get(fileId);
        // Only remove if file is NOT in repository AND is completed
        // If file is still processing, keep it in state
        if (
          !repositoryFileIds.has(fileId) &&
          fileState?.status === 'completed'
        ) {
          store.set(fileUploadSegment, prev =>
            removeFileFromState(prev, fileId)
          );
        }
      });

      // Update each file with repository data or add new files from repository
      data.fileRepositoryDetails.forEach(repoFile => {
        const existingFile = files.get(repoFile.fileId);

        if (existingFile) {
          // Map extraction status to file state
          const extractionResult = mapExtractionStatusToError(
            repoFile.extractionStatus
          );

          // Update existing file with repository data and extraction status
          store.set(fileUploadSegment, prev =>
            updateFileState(prev, repoFile.fileId, {
              description: repoFile.description,
              customFileName: repoFile.customFileName,
              thumbnail: repoFile.fileMetadata.thumbnail,
              extractedMetadata: {
                brand: repoFile.extractedMetadata.brand,
                country: repoFile.extractedMetadata.country,
              },
              // Only update status/error if extraction status indicates an error
              // This prevents overwriting in-progress states during polling
              ...(extractionResult.status === 'error' && {
                status: extractionResult.status,
                error: extractionResult.error,
              }),
            })
          );
        } else {
          // Add new file from repository to state (for files uploaded in previous sessions)
          // Map extraction status to determine the file state
          const extractionResult = mapExtractionStatusToError(
            repoFile.extractionStatus
          );

          const newFileState: FileState = {
            fileId: repoFile.fileId,
            fileName: repoFile.fileName,
            fileSize: repoFile.fileMetadata.fileSize, // Keep as string from API (e.g., "23.01 MB")
            status: extractionResult.status,
            uploadProgress: 100,
            extractionProgress:
              extractionResult.status === 'completed' ? 100 : 0,
            retryCount: 0,
            isDeleting: false,
            description: repoFile.description,
            customFileName: repoFile.customFileName,
            thumbnail: repoFile.fileMetadata.thumbnail,
            extractedMetadata: {
              brand: repoFile.extractedMetadata.brand,
              country: repoFile.extractedMetadata.country,
            },
            error: extractionResult.error,
          };

          store.set(fileUploadSegment, prev => ({
            ...prev,
            files: new Map(prev.files).set(repoFile.fileId, newFileState),
          }));
        }
      });

      // Clear loading state and reset retry count on success
      store.set(fileUploadSegment, prev => ({
        ...prev,
        isLoadingRepository: false,
        repositoryFetchError: false,
        repositoryRetryCount: 0,
      }));

      return true;
    }

    // Set error state if response was not successful and increment retry count
    store.set(fileUploadSegment, prev => ({
      ...prev,
      isLoadingRepository: false,
      repositoryFetchError: true,
      repositoryRetryCount: prev.repositoryRetryCount + 1,
    }));

    return false;
  } catch {
    Logger.error(new Error('Failed to fetch file repository'));

    // Set error state on exception and increment retry count
    store.set(fileUploadSegment, prev => ({
      ...prev,
      isLoadingRepository: false,
      repositoryFetchError: true,
      repositoryRetryCount: prev.repositoryRetryCount + 1,
    }));

    return false;
  }
};

/**
 * Downloads selected files using presigned URL.
 * Returns success status and message for toast notifications.
 *
 * @param {string} sessionId - The session ID associated with the files.
 * @param {string[]} fileIds - Array of file IDs to download.
 * @returns {Promise<{ success: boolean; message: string }>} - Download result with message for toast.
 */
export const downloadFiles = async (
  sessionId: string,
  fileIds: string[]
): Promise<{ success: boolean; message: string }> => {
  if (!sessionId || fileIds.length === 0) {
    return {
      success: false,
      message: translate('file_download_no_files_selected'),
    };
  }

  try {
    // Get presigned download URL from API
    const response = await getFileDownloadUrlApiCall(sessionId, fileIds);

    if (response.success && response.data?.fileDownload?.url) {
      const { url: presignedUrl } = response.data.fileDownload;

      // Get file names for download
      const { files } = store.get(fileUploadSegment);
      const fileNames = fileIds
        .map(fileId => {
          const file = files.get(fileId);
          return file?.customFileName || file?.fileName || `file-${fileId}`;
        })
        .join('_');

      // Download file from presigned URL
      const downloadResult = await downloadFileFromPresignedUrl(
        presignedUrl,
        fileNames
      );

      if (downloadResult.success) {
        // Track analytics for file download
        trackFileDownload();

        return {
          success: true,
          message: translate('file_download_success_message'),
        };
      } else {
        return {
          success: false,
          message: translate('file_download_failed_message'),
        };
      }
    }

    // API returned failure
    return {
      success: false,
      message:
        response.message || translate('file_download_url_generation_failed'),
    };
  } catch (error) {
    Logger.error(new Error(`File download failed: ${String(error)}`));
    return {
      success: false,
      message: translate('file_download_error_message'),
    };
  }
};
